// src/components/RecentViews.tsx
import React from 'react';
import { Typography, Empty, Button, Image, Space, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

export type RecentItem = {
  id: string;
  category: 'Катушки' | 'Свечи' | 'Проводки' | 'Датчики' | 'Шлейфы';
  brand?: string;
  partNumber?: string;
  model?: string;
  original?: string; // 'original' | 'copy'
  price?: number | null;
  thumb?: string | null;
  quantity?: number | null;
};

interface Props {
  items: RecentItem[];
  onClear?: () => void;
}

const categoryRoutes: Record<RecentItem['category'], string> = {
  'Катушки': '/products/coils',
  'Свечи': '/products/plugs',
  'Проводки': '/products/wires',
  'Датчики': '/products/sensors',
  'Шлейфы': '/products/clocks',
};

const RecentViews: React.FC<Props> = ({ items, onClear }) => {
  const navigate = useNavigate();

  const handleCardClick = (it: RecentItem) => {
    localStorage.setItem('highlightProductId', it.id);
    navigate(categoryRoutes[it.category]);
  };

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Title level={5} style={{ margin: 0 }}>🕓 Недавние просмотры</Title>
        {items.length > 0 && (
          <Button size="small" onClick={onClear}>Очистить</Button>
        )}
      </div>

      {items.length === 0 ? (
        <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 24, textAlign: 'center' }}>
          <Empty description="Пока нет истории" />
        </div>
      ) : (
        <div
          style={{
            border: '1px solid #f0f0f0',
            borderRadius: 8,
            padding: 16,
            maxHeight: 730,
            overflowY: 'auto',
            backgroundColor: '#fafafa'
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 16,
            }}
          >
            {items.map((it) => {
              const typeLabel =
                it.original === 'original' ? 'Оригинал' :
                it.original === 'copy' ? 'Копия' : '';

              return (
                <div
                  key={`${it.category}-${it.id}`}
                  onClick={() => handleCardClick(it)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: '#fff',
                    border: '1px solid #e8e8e8',
                    borderRadius: 8,
                    padding: 12,
                    cursor: 'pointer',
                    transition: 'box-shadow 0.2s, border-color 0.2s',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#1677ff')}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#e8e8e8')}
                >
                  {/* Блок изображения */}
                  <div style={{ height: 120, display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 12, backgroundColor: '#f5f5f5', borderRadius: 6, overflow: 'hidden' }}>
                    {it.thumb ? (
                      <Image src={it.thumb} alt={it.partNumber} preview={false} style={{ maxHeight: 120, objectFit: 'contain' }} />
                    ) : (
                      <Text type="secondary" style={{ fontSize: 12 }}>нет фото</Text>
                    )}
                  </div>

                  {/* Информационный блок */}
                  <Space direction="vertical" size={2} style={{ width: '100%', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                      <Text strong ellipsis={{ tooltip: true }} style={{ maxWidth: '70%' }}>
                        {it.brand || '—'}
                      </Text>
                      {it.quantity != null && (
                        <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                          {Number(it.quantity)} шт.
                        </Text>
                      )}
                    </div>

                    <Text ellipsis={{ tooltip: true }} style={{ width: '100%', fontSize: 13 }}>
                      {it.partNumber || 'Без номера'}
                    </Text>
                    
                    <Text type="secondary" ellipsis={{ tooltip: true }} style={{ width: '100%', fontSize: 12 }}>
                      {it.model || '—'}
                    </Text>
                  </Space>

                  {/* Подвал карточки */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: 12 }}>
                    {typeLabel ? (
                      <Tag color={it.original === 'original' ? 'blue' : 'default'} style={{ margin: 0 }}>
                        {typeLabel}
                      </Tag>
                    ) : <span />}
                    
                    <Text strong style={{ color: '#1677ff', fontSize: 14 }}>
                      {it.price != null ? `${it.price.toLocaleString('ru-RU')} ֏` : '—'}
                    </Text>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecentViews;